import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { CheckCircle2, Clock, XCircle, AlertCircle, AlertTriangle, TrendingUp, ClipboardList, Receipt, RefreshCw, ChevronRight, CalendarDays, Store } from "lucide-react";

const STATUS_CONFIG = {
  completado: { label: "Completado", color: "bg-green-100 text-green-800", icon: CheckCircle2 },
  en_progreso: { label: "En progreso", color: "bg-yellow-100 text-yellow-800", icon: Clock },
  pendiente: { label: "Pendiente", color: "bg-slate-100 text-slate-600", icon: Clock },
  firmado: { label: "Firmado", color: "bg-green-100 text-green-800", icon: CheckCircle2 },
  borrador: { label: "Borrador", color: "bg-yellow-100 text-yellow-800", icon: Clock },
};

function StatusChip({ status, label }) {
  const cfg = STATUS_CONFIG[status] || { label: status, color: "bg-slate-100 text-slate-600", icon: Clock };
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${cfg.color}`}>
      <Icon className="w-3 h-3" />
      {label || cfg.label}
    </span>
  );
}

function KpiBar({ counts }) {
  const total = counts.verde + counts.amarillo + counts.rojo;
  if (total === 0) return <span className="text-xs text-slate-400">Sin registros</span>;
  return (
    <div className="flex items-center gap-2">
      {counts.verde > 0 && <span className="text-xs font-medium text-green-700">{counts.verde} verde</span>}
      {counts.amarillo > 0 && <span className="text-xs font-medium text-yellow-700">{counts.amarillo} amarillo</span>}
      {counts.rojo > 0 && <span className="text-xs font-medium text-red-700">{counts.rojo} rojo</span>}
    </div>
  );
}

export default function OpsDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [kpiPending, setKpiPending] = useState(null);
  const [branchOpsPending, setBranchOpsPending] = useState(0);

  const KPI_ROLES = ["corporate_admin", "branch_manager", "treasury", "legal", "hr_admin"];

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const res = await api.get(`/ops/dashboard?date=${date}`);
      setData(res.data);
    } catch (e) {
      setData([]);
      setLoadError(
        e.response?.status === 403
          ? "No tienes acceso a este panel."
          : "No se pudo cargar el panel. Intenta de nuevo."
      );
    } finally {
      setLoading(false);
    }
  }, [date]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!user || !KPI_ROLES.includes(user.role)) return;
    api.get("/ops/kpis/my-pending")
      .then(r => setKpiPending(r.data.total_pending || 0))
      .catch(() => setKpiPending(0));
  }, [user]);

  useEffect(() => {
    if (!user || !["corporate_admin", "hr_admin"].includes(user.role)) return;
    api.get("/suppliers/reports/branch-operations-status")
      .then(r => setBranchOpsPending((r.data?.list_pending || []).length))
      .catch(() => setBranchOpsPending(0));
  }, [user]);

  const CHECKLIST_CATEGORIES = ["procesos", "limpieza", "supervision"];
  const CATEGORY_LABEL = { procesos: "Procesos", limpieza: "Limpieza", supervision: "Supervisión" };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Panel Operativo</h1>
          <p className="text-sm text-slate-500">Estado del dia por sucursal</p>
        </div>
        <div className="flex items-center gap-2">
          <CalendarDays className="w-4 h-4 text-slate-400" />
          <input
            type="date"
            value={date}
            onChange={e => setDate(e.target.value)}
            className="border border-slate-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]"
            data-testid="ops-date-picker"
          />
          <button
            onClick={load}
            className="p-2 rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors"
            data-testid="ops-refresh-btn"
          >
            <RefreshCw className="w-4 h-4 text-slate-500" />
          </button>
        </div>
      </div>

      {/* Widget KPIs pendientes */}
      {kpiPending !== null && (
        <div
          className={`rounded-xl border px-4 py-3 flex items-center justify-between ${
            kpiPending === 0 ? "bg-green-50 border-green-200" : "bg-yellow-50 border-yellow-300"
          }`}
          data-testid="ops-kpi-widget"
        >
          <div className="flex items-center gap-3">
            <TrendingUp className={`w-5 h-5 ${kpiPending === 0 ? "text-green-500" : "text-yellow-600"}`} />
            <div>
              <span className={`text-sm font-semibold ${kpiPending === 0 ? "text-green-800" : "text-yellow-800"}`}>
                {kpiPending === 0 ? "Todos los KPIs del dia al dia" : `${kpiPending} KPIs pendientes hoy`}
              </span>
              {kpiPending === 0 && (
                <span className="ml-2 text-green-600 text-xs font-medium">Completado</span>
              )}
            </div>
          </div>
          {kpiPending > 0 && (
            <button
              onClick={() => navigate("/ops/kpis")}
              className="px-3 py-1.5 bg-yellow-400 hover:bg-yellow-500 text-slate-900 text-xs font-semibold rounded-lg transition-colors"
              data-testid="ops-kpi-widget-btn"
            >
              Cargar ahora
            </button>
          )}
        </div>
      )}

      {/* Card — Proveedores multi-sucursal sin operaciones configuradas (Claudia/Gustavo) */}
      {branchOpsPending > 0 && (
        <div
          className="rounded-xl border bg-amber-50 border-amber-300 px-4 py-3 flex items-center justify-between gap-3"
          data-testid="ops-branch-ops-pending-widget"
        >
          <div className="flex items-center gap-3">
            <Store className="w-5 h-5 text-amber-600 flex-shrink-0" />
            <div>
              <span className="text-lg font-bold text-amber-900">{branchOpsPending}</span>
              <span className="ml-2 text-sm text-amber-800">
                proveedor{branchOpsPending === 1 ? "" : "es"} esperan configuración de operaciones por sucursal
              </span>
            </div>
          </div>
          <button
            onClick={() => navigate("/proveedores/pendientes-configuracion")}
            className="px-3 py-1.5 bg-amber-400 hover:bg-amber-500 text-slate-900 text-xs font-semibold rounded-lg transition-colors flex-shrink-0"
            data-testid="ops-branch-ops-pending-btn"
          >
            Ver todos
          </button>
        </div>
      )}

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="bg-white rounded-xl border border-slate-200 p-5 animate-pulse h-52" />
          ))}
        </div>
      ) : loadError ? (
        <div className="text-center py-16 text-slate-500 space-y-2" data-testid="ops-dashboard-error">
          <AlertTriangle className="w-8 h-8 text-red-400 mx-auto" />
          <p>{loadError}</p>
        </div>
      ) : data.length === 0 ? (
        <div className="text-center py-16 text-slate-400">Sin datos para esta fecha</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {data.map(branch => (
            <div
              key={branch.branch_id}
              className="bg-white rounded-xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow p-5 space-y-4"
              data-testid={`ops-branch-card-${branch.branch_id}`}
            >
              <div className="flex items-start justify-between">
                <div>
                  <h2 className="font-bold text-slate-900 text-lg">{branch.branch_name}</h2>
                  {!branch.has_manager ? (
                    <span className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-orange-100 text-orange-700" data-testid={`ops-no-manager-badge-${branch.branch_id}`}>
                      <AlertCircle className="w-3 h-3" /> Sin gerente asignado
                    </span>
                  ) : (
                    <span className="text-xs text-slate-500">{branch.manager_name}</span>
                  )}
                </div>
                {branch.venta_dia != null && (
                  <div className="text-right">
                    <div className="text-sm text-slate-400">Venta ({branch.corte_date})</div>
                    <div className="font-bold text-slate-900">
                      {new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN", maximumFractionDigits: 0 }).format(branch.venta_dia)}
                    </div>
                  </div>
                )}
              </div>

              {branch.active_alerts_count > 0 && (
                <div className="rounded-lg border border-red-200 bg-red-50 p-2.5 space-y-1" data-testid={`ops-branch-alerts-${branch.branch_id}`}>
                  <div className="text-xs font-semibold text-red-700 uppercase tracking-wide flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" /> {branch.active_alerts_count} alerta{branch.active_alerts_count > 1 ? "s" : ""} activa{branch.active_alerts_count > 1 ? "s" : ""}
                  </div>
                  {branch.active_alerts.slice(0, 2).map(a => (
                    <div key={a.id} className="text-xs text-red-800 truncate">{a.title}</div>
                  ))}
                </div>
              )}

              <div>
                <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2 flex items-center gap-1">
                  <ClipboardList className="w-3 h-3" /> Checklists
                </div>
                <div className="flex flex-wrap gap-2">
                  {CHECKLIST_CATEGORIES.map(cat => {
                    const agg = branch.checklists[cat];
                    const pct = agg && agg.total > 0 ? Math.round((agg.completed / agg.total) * 100) : null;
                    return (
                      <button
                        key={cat}
                        onClick={() => navigate(`/ops/checklists?category=${cat}`)}
                        disabled={!agg}
                        className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium border transition-colors ${
                          agg ? "hover:bg-slate-50 cursor-pointer border-slate-200" : "border-dashed border-slate-200 text-slate-400 cursor-default"
                        }`}
                        data-testid={`ops-checklist-chip-${branch.branch_id}-${cat}`}
                      >
                        {agg ? (
                          <span className={pct === 100 ? "text-green-700" : pct > 0 ? "text-yellow-700" : "text-slate-500"}>
                            {CATEGORY_LABEL[cat]}: {agg.completed}/{agg.total}
                          </span>
                        ) : (
                          <span className="text-slate-400">{CATEGORY_LABEL[cat]}: sin generar</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1 flex items-center gap-1">
                    <Receipt className="w-3 h-3" /> Corte ({branch.corte_date})
                  </div>
                  {branch.corte_id ? (
                    <button onClick={() => navigate(`/ops/cortes/${branch.corte_id}`)} className="hover:underline">
                      <StatusChip status={branch.corte} />
                    </button>
                  ) : (
                    <span className="text-xs text-slate-400">Sin corte</span>
                  )}
                </div>
                <div>
                  <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1 flex items-center gap-1">
                    <TrendingUp className="w-3 h-3" /> KPIs
                  </div>
                  <KpiBar counts={branch.kpis_resumen} />
                </div>
              </div>

              <button
                onClick={() => navigate(`/ops/checklists?branch_id=${branch.branch_id}`)}
                className="w-full flex items-center justify-center gap-1 py-2 rounded-lg bg-[#FFDF00] hover:bg-yellow-300 transition-colors text-sm font-semibold text-slate-900"
                data-testid={`ops-branch-detail-btn-${branch.branch_id}`}
              >
                Ver detalle <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      {user?.role === "corporate_admin" && (
        <div className="flex gap-2 pt-2 border-t border-slate-100">
          <button
            onClick={async () => {
              try {
                await api.post("/ops/checklists/generate", { date });
                load();
              } catch {/* silent */}
            }}
            className="text-xs text-slate-500 hover:text-slate-800 transition-colors underline"
            data-testid="ops-generate-checklists-btn"
          >
            Generar checklists para esta fecha
          </button>
        </div>
      )}
    </div>
  );
}
